<template>
    <div class="ds-calendar" :class="{
        'ds-calendar-large': props.large,
        'ds-calendar-copy-states': props.copyStateStyles,
        'ds-calendar-with-presets': showPresets,
    }">
        <!-- Период: быстрый выбор слева, применяется сразу полным диапазоном (RD-160). -->
        <div v-if="showPresets" class="ds-calendar-presets" role="group" aria-label="Быстрый выбор">
            <div class="ds-calendar-presets-title">Быстрый выбор</div>
            <button
                v-for="preset in presetItems"
                :key="preset.id"
                type="button"
                class="ds-calendar-preset"
                :class="{ 'is-selected': preset.isSelected }"
                :aria-pressed="preset.isSelected"
                :disabled="preset.isDisabled"
                @click="selectPreset(preset)">
                {{ preset.label }}
            </button>
        </div>

        <div class="ds-calendar-body">
            <!-- Выбор месяца: precision='month' или клик по заголовку месяца. Кварталы — только группировка. -->
            <template v-if="isMonthView">
                <div class="ds-calendar-header">
                    <button type="button" class="ds-calendar-nav-button" aria-label="Предыдущий год" @click="pickerYear--">
                        <icon width="9" height="13"><IconChevronLeft /></icon>
                    </button>
                    <span class="ds-calendar-title">{{ pickerYear }}</span>
                    <button type="button" class="ds-calendar-nav-button" aria-label="Следующий год" @click="pickerYear++">
                        <icon width="9" height="13"><IconChevronRight /></icon>
                    </button>
                </div>
                <div class="ds-calendar-quarters">
                    <div v-for="(quarter, q) in CALENDAR_QUARTERS" :key="quarter" class="ds-calendar-quarter">
                        <span class="ds-calendar-quarter-label">{{ quarter }}</span>
                        <button
                            v-for="month in [q * 3, q * 3 + 1, q * 3 + 2]"
                            :key="month"
                            type="button"
                            class="ds-calendar-month-item"
                            :class="{
                                'ds-calendar-month-item-selected': isPickerSelected(month),
                                'ds-calendar-month-item-current': isCurrentMonth(month),
                            }"
                            :aria-pressed="isPickerSelected(month)"
                            @click="onMonthClick(month)">
                            {{ CALENDAR_MONTH_NAMES[month] }}
                        </button>
                    </div>
                </div>
            </template>

            <!-- Дни (precision='day', по умолчанию) и выбор недели (precision='week') -->
            <template v-else>
                <div class="ds-calendar-header">
                    <button type="button" class="ds-calendar-nav-button" aria-label="Предыдущий месяц" @click="prevMonth">
                        <icon width="9" height="13"><IconChevronLeft /></icon>
                    </button>
                    <button type="button" class="ds-calendar-title ds-calendar-title-clickable" @click="openMonthPicker">
                        {{ headerLabel }}
                    </button>
                    <button type="button" class="ds-calendar-nav-button" aria-label="Следующий месяц" @click="nextMonth">
                        <icon width="9" height="13"><IconChevronRight /></icon>
                    </button>
                </div>

                <!-- Выбор недели: строка «номер + Пн–Вс» — одна кнопка, значение — понедельник недели -->
                <template v-if="precision === 'week'">
                    <div class="ds-calendar-week-row ds-calendar-week-head" aria-hidden="true">
                        <span class="ds-calendar-week-number">нед</span>
                        <span v-for="d in CALENDAR_WEEK_DAYS" :key="d" class="ds-calendar-weekday">{{ d }}</span>
                    </div>
                    <div class="ds-calendar-rows">
                        <button
                            v-for="week in weeks"
                            :key="week.start.valueOf()"
                            type="button"
                            class="ds-calendar-week-row ds-calendar-week"
                            :class="{ 'ds-calendar-week-selected': week.isSelected }"
                            :aria-label="`Неделя ${week.number}: ${week.days[0].date.format('D MMMM')} – ${week.days[6].date.format('D MMMM YYYY')}`"
                            :aria-pressed="week.isSelected"
                            @click="emit('update:modelValue', week.start)">
                            <span class="ds-calendar-week-number">{{ week.number }}</span>
                            <span v-for="cell in week.days" :key="cell.key" class="ds-calendar-week-cell">
                                <span
                                    class="ds-calendar-week-day"
                                    :class="{
                                        'ds-calendar-week-day-muted': !cell.currentMonth,
                                        'ds-calendar-week-day-today': cell.isToday,
                                    }">
                                    {{ cell.day }}
                                </span>
                            </span>
                        </button>
                    </div>
                </template>

                <template v-else>
                    <div class="ds-calendar-weekdays">
                        <div v-for="d in CALENDAR_WEEK_DAYS" :key="d" class="ds-calendar-weekday">{{ d }}</div>
                    </div>

                    <div class="ds-calendar-rows">
                        <div v-for="(row, r) in dayRows" :key="r" class="ds-calendar-grid">
                            <div
                                v-for="cell in row"
                                :key="cell.key"
                                class="ds-calendar-cell"
                                :class="cell.classes"
                                :data-copy-state="cell.copyState"
                                :aria-disabled="cell.isDisabled"
                                @click="onDayClick(cell)">
                                <span class="ds-calendar-day-wrap">
                                    <SmallTooltip v-if="cell.tooltip" :text="cell.tooltip">
                                        <span class="ds-calendar-day">{{ cell.day }}</span>
                                    </SmallTooltip>
                                    <span v-else class="ds-calendar-day">{{ cell.day }}</span>
                                </span>
                                <span
                                    v-if="props.large && cell.label"
                                    class="ds-calendar-day-label"
                                    :class="{ 'ds-calendar-day-label-chip': Boolean(cell.label.background) }"
                                    :style="cell.label.background ? { backgroundColor: cell.label.background } : undefined">
                                    {{ cell.label.text }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import IconChevronLeft from './icons/IconChevronLeft.vue'
import IconChevronRight from './icons/IconChevronRight.vue'
import SmallTooltip from '@/components/SmallTooltip.vue'
import {
    CALENDAR_MONTH_NAMES,
    CALENDAR_QUARTERS,
    CALENDAR_WEEK_DAYS,
    calendarDateKey,
    calendarMonthStart,
    calendarRangePresets,
    type CalendarDayLabel,
    type CalendarRangePreset,
} from './calendar'
import { refreshCalendarClock, useCalendarToday } from './calendarTime'

dayjs.extend(isoWeek)

type Mode = 'single' | 'range' | 'multi'
type Precision = 'day' | 'month' | 'week'

interface Cell {
    key: string
    date: Dayjs
    day: number
    currentMonth: boolean
    isToday: boolean
}

interface DayCell extends Cell {
    isDisabled: boolean
    classes: string[]
    copyState?: string
    tooltip?: string
    label?: CalendarDayLabel
}

const props = withDefaults(defineProps<{
    modelValue?: Dayjs | [Dayjs, Dayjs] | Dayjs[] | null
    mode?: Mode
    precision?: Precision
    disabledDates?: Dayjs[]
    sourceDates?: Dayjs[]
    occupiedDates?: Dayjs[]
    hrDeviationDates?: Dayjs[]
    hrDeviationLabels?: Record<string, string>
    minDate?: Dayjs
    initialDate?: Dayjs
    large?: boolean
    copyStateStyles?: boolean
    // RD-160: колонка быстрого выбора (только mode='range').
    presets?: boolean
    // RD-160: подписи под числом в large, ключ — YYYY-MM-DD.
    dayLabels?: Record<string, CalendarDayLabel>
    // RD-160: дни соседних месяцев выбираются, если их не запрещают ограничения consumer.
    outsideDaysSelectable?: boolean
}>(), {
    mode: 'single',
    precision: 'day',
    presets: false,
    outsideDaysSelectable: true,
})

const emit = defineEmits<{
    'update:modelValue': [value: Dayjs | [Dayjs, Dayjs] | Dayjs[]]
}>()

// «Сегодня» и границы preset — календарное время принятого профиля (D-001), реактивно:
// полночь, возврат из фона, смена timezone/fallback. cursor и выбранные даты при этом не меняются.
const today = useCalendarToday()

const cursor = ref((props.initialDate ?? today.value).startOf('month'))
const showMonthPicker = ref(false)
const pickerYear = ref(
    props.precision === 'month'
        ? ((props.modelValue as Dayjs)?.year() ?? today.value.year())
        : cursor.value.year()
)

const isMonthView = computed(() => props.precision === 'month' || showMonthPicker.value)
const showPresets = computed(() => props.presets && props.mode === 'range' && props.precision === 'day')

// «Октябрь 2026»: именительный падеж (формат без числа), с заглавной буквы.
const headerLabel = computed(() => {
    const label = cursor.value.format('MMMM YYYY')
    return label.charAt(0).toUpperCase() + label.slice(1)
})

function openMonthPicker() {
    pickerYear.value = cursor.value.year()
    showMonthPicker.value = true
}

function isPickerSelected(month: number): boolean {
    if (props.precision === 'month') {
        const mv = props.modelValue as Dayjs | null | undefined
        return !!mv && mv.month() === month && mv.year() === pickerYear.value
    }
    return cursor.value.month() === month && cursor.value.year() === pickerYear.value
}

function isCurrentMonth(month: number) {
    return today.value.month() === month && today.value.year() === pickerYear.value
}

function onMonthClick(month: number) {
    const value = calendarMonthStart(pickerYear.value, month)
    cursor.value = value
    if (props.precision === 'month') {
        // Выбор месяца как конечного значения
        emit('update:modelValue', value)
    } else {
        // Оверлей выбора месяца в дневном/недельном режиме — только навигация
        showMonthPicker.value = false
    }
}

const cells = computed<Cell[]>(() => {
    const start = cursor.value.startOf('month')
    const startWeekday = (start.day() + 6) % 7
    const daysInMonth = start.daysInMonth()
    const result: Cell[] = []
    const push = (date: Dayjs, currentMonth: boolean) => result.push({
        key: calendarDateKey(date),
        date,
        day: date.date(),
        currentMonth,
        isToday: date.isSame(today.value, 'day'),
    })

    for (let i = startWeekday; i > 0; i--) push(start.subtract(i, 'day'), false)
    for (let d = 0; d < daysInMonth; d++) push(start.add(d, 'day'), true)
    const remaining = (7 - (result.length % 7)) % 7
    for (let i = 0; i < remaining; i++) push(start.add(daysInMonth + i, 'day'), false)
    return result
})

// precision='week': строки сетки месяца (они всегда начинаются с понедельника).
const weeks = computed(() => {
    const selected = props.modelValue as Dayjs | null | undefined
    const result: { start: Dayjs, number: number, isSelected: boolean, days: Cell[] }[] = []
    for (let i = 0; i < cells.value.length; i += 7) {
        const days = cells.value.slice(i, i + 7)
        const start = days[0].date.startOf('day')
        result.push({
            start,
            number: start.isoWeek(),
            isSelected: Boolean(selected && selected.isSame(start, 'day')),
            days,
        })
    }
    return result
})

const keySet = (dates?: Dayjs[]) => new Set((dates ?? []).filter(Boolean).map(calendarDateKey))
const sourceKeys = computed(() => keySet(props.sourceDates))
const occupiedKeys = computed(() => keySet(props.occupiedDates))
const hrDeviationKeys = computed(() => keySet(props.hrDeviationDates))
const disabledKeys = computed(() => keySet(props.disabledDates))

const rangeValue = computed<[Dayjs | null, Dayjs | null]>(() => {
    if (props.mode !== 'range' || !props.modelValue) return [null, null]
    const [start, end] = props.modelValue as [Dayjs | null, Dayjs | null]
    return [start ?? null, end ?? null]
})

const selectedKeys = computed(() => {
    if (!props.modelValue) return new Set<string>()
    if (props.mode === 'single') return keySet([props.modelValue as Dayjs])
    if (props.mode === 'multi') return keySet(props.modelValue as Dayjs[])
    const [start, end] = rangeValue.value
    return keySet([start, end].filter((date): date is Dayjs => Boolean(date)))
})

function isDateDisabled(date: Dayjs) {
    const key = calendarDateKey(date)
    if (props.minDate && date.isBefore(props.minDate, 'day')) return true
    return hrDeviationKeys.value.has(key) || sourceKeys.value.has(key) || disabledKeys.value.has(key)
}

function rangeClasses(cell: Cell): string[] {
    const [start, end] = rangeValue.value
    if (!start) return []
    const isStart = cell.date.isSame(start, 'day')
    const isEnd = Boolean(end && cell.date.isSame(end, 'day'))
    if (!end || start.isSame(end, 'day')) return isStart ? ['is-selected'] : []
    const classes: string[] = []
    if (isStart) classes.push('is-selected', 'is-range-start')
    else if (isEnd) classes.push('is-selected', 'is-range-end')
    else if (cell.date.isAfter(start, 'day') && cell.date.isBefore(end, 'day')) classes.push('is-in-range')
    return classes
}

function dayTooltip(cell: Cell, isHrDeviation: boolean, isSource: boolean, isOccupied: boolean) {
    if (!cell.currentMonth) return undefined
    if (isHrDeviation) return props.hrDeviationLabels?.[cell.key] || 'Кадровое отклонение'
    if (isSource) return undefined
    if (isOccupied) return 'Есть смены'
    return undefined
}

function copyState(isHrDeviation: boolean, isSource: boolean, isDisabled: boolean, isSelected: boolean, isOccupied: boolean) {
    if (isHrDeviation) return 'occupied hr-deviation'
    if (isSource) return 'source'
    if (isDisabled) return undefined
    if (isSelected) return isOccupied ? 'selected selected-occupied' : 'selected'
    if (isOccupied) return 'occupied'
    return undefined
}

const dayRows = computed<DayCell[][]>(() => {
    const rows: DayCell[][] = []
    cells.value.forEach((cell, index) => {
        const isHrDeviation = cell.currentMonth && hrDeviationKeys.value.has(cell.key)
        const isSource = cell.currentMonth && sourceKeys.value.has(cell.key)
        const isOccupied = cell.currentMonth && occupiedKeys.value.has(cell.key)
        const isDisabled = isDateDisabled(cell.date) || (!cell.currentMonth && !props.outsideDaysSelectable)
        const isSelected = selectedKeys.value.has(cell.key)
        const classes: string[] = []

        if (!cell.currentMonth) classes.push('is-outside')
        if (isDisabled) classes.push('is-disabled')
        if (cell.isToday) classes.push('is-today')
        if (index % 7 === 0) classes.push('is-row-start')
        if (index % 7 === 6) classes.push('is-row-end')
        if (props.mode === 'range') classes.push(...rangeClasses(cell))
        else if (isSelected && !isDisabled) classes.push('is-selected')

        const dayCell: DayCell = {
            ...cell,
            isDisabled,
            classes,
            tooltip: dayTooltip(cell, isHrDeviation, isSource, isOccupied),
            label: cell.currentMonth ? props.dayLabels?.[cell.key] : undefined,
        }
        if (cell.currentMonth && props.copyStateStyles) {
            dayCell.copyState = copyState(isHrDeviation, isSource, isDisabled, isSelected, isOccupied)
        }
        if (index % 7 === 0) rows.push([])
        rows[rows.length - 1].push(dayCell)
    })
    return rows
})

function onDayClick(cell: DayCell) {
    if (cell.isDisabled) return
    if (props.mode === 'single') {
        emit('update:modelValue', cell.date)
    } else if (props.mode === 'multi') {
        const current = (props.modelValue as Dayjs[]) ?? []
        const idx = current.findIndex(d => d.isSame(cell.date, 'day'))
        emit('update:modelValue', idx > -1
            ? current.filter((_, i) => i !== idx)
            : [...current, cell.date])
    } else if (props.mode === 'range') {
        // Первый клик — начало, второй завершает и применяет период.
        const [start, end] = rangeValue.value
        if (!start || end) {
            emit('update:modelValue', [cell.date, null as unknown as Dayjs])
        } else {
            emit('update:modelValue', cell.date.isBefore(start, 'day')
                ? [cell.date, start]
                : [start, cell.date])
        }
    }
}

const presetItems = computed(() => {
    const [start, end] = rangeValue.value
    return calendarRangePresets(today.value).map(preset => ({
        ...preset,
        isSelected: Boolean(start && end && start.isSame(preset.start, 'day') && end.isSame(preset.end, 'day')),
        // Ограничения consumer (min/disabled) быстрый выбор не обходит.
        isDisabled: Boolean(
            (props.minDate && preset.start.isBefore(props.minDate, 'day'))
            || props.disabledDates?.some(date => !date.isBefore(preset.start, 'day') && !date.isAfter(preset.end, 'day'))
        ),
    }))
})

// Границы считаются заново от свежего «сейчас»: после полуночи «Этот месяц» — уже новый месяц.
function selectPreset(preset: CalendarRangePreset) {
    refreshCalendarClock()
    const current = calendarRangePresets(today.value).find(item => item.id === preset.id) ?? preset
    cursor.value = current.end.startOf('month')
    emit('update:modelValue', [current.start, current.end])
}

function prevMonth() { cursor.value = cursor.value.subtract(1, 'month') }
function nextMonth() { cursor.value = cursor.value.add(1, 'month') }
</script>
